import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Table, Select, Input, Space, Tag, Button, Card, Row, Col, Statistic, Typography, Alert, Tooltip, message,
} from 'antd';
import { ReloadOutlined, FileExcelOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import { fetchWithRetry } from '../utils/fetchWithRetry';

/*
 * Admin > Reports > Reminder History
 * The review reminder emails sent by the API (BackgroundServices/CampaignReminderService.cs):
 * 5 pm IST two days before each stage of the active campaign ends and on its last day, to people
 * who still had a review waiting. One row per run (stage + date), expandable to who got it.
 */

const { Text } = Typography;
const BASE = process.env.REACT_APP_API_URL;

type StageKey = 'manager' | 'first_level' | 'second_level' | 'elt_ranking';
type Status = 'Sent' | 'Failed' | 'Suppressed';

interface Person {
  runId: number;
  email: string;
  name: string | null;
  status: Status;
  error: string | null;
  sentAt: string;
}

interface Run {
  id: number;
  stage: StageKey;
  reminderDate: string;
  claimedAt: string;
  finishedAt: string | null;
  recipients: number;
  failed: number;
  people: Person[];
}

interface CampaignOption {
  id: number;
  campaignName: string | null;
  campaignStatus: string | null;
}

const STAGES: Record<StageKey, { label: string; color: string }> = {
  manager:      { label: "People's manager",  color: 'blue' },
  first_level:  { label: 'Unit Head (Level 1)', color: 'geekblue' },
  second_level: { label: 'ELT approver (Level 2)', color: 'purple' },
  elt_ranking:  { label: 'ELT stack ranking', color: 'magenta' },
};

const STATUS: Record<Status, { label: string; color: string; help: string }> = {
  Sent:       { label: 'Sent',            color: 'green',  help: 'Delivered to the mail server.' },
  Failed:     { label: 'Failed',          color: 'red',    help: 'The email could not be sent.' },
  Suppressed: { label: 'Not sent (non-prod)', color: 'orange', help: 'Built but not delivered: emails are only delivered from production.' },
};

// All reminder times are shown in IST, the time zone the reminders are scheduled in
const ist = (v: string | null | undefined, withTime = true) =>
  v ? new Date(v).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit', hour12: true } : {}),
  }) : '—';
const dateOnly = (v: string) => new Date(`${v}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

const ReminderHistory: React.FC = () => {
  const { instance, accounts } = useMsal();
  const [campaigns, setCampaigns] = useState<CampaignOption[]>([]);
  const [campaignId, setCampaignId] = useState<number | null>(null);
  const [runs, setRuns] = useState<Run[]>([]);
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState<StageKey | undefined>();
  const [status, setStatus] = useState<Status | undefined>();
  const [search, setSearch] = useState('');

  const get = useCallback(async (path: string) => {
    const res = await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
    const r = await fetchWithRetry(`${BASE}${path}`, { headers: { Authorization: `Bearer ${res.idToken}` } });
    if (!r.ok) throw new Error((await r.text()).replace(/^"|"$/g, '') || `Request failed (${r.status}).`);
    return r.json();
  }, [instance, accounts]);

  // Campaign list; start on the active campaign
  useEffect(() => {
    get('/api/campaigns')
      .then((list: CampaignOption[]) => {
        setCampaigns(list ?? []);
        const active = (list ?? []).find(c => c.campaignStatus === 'active') ?? (list ?? [])[0];
        setCampaignId(active ? active.id : 0);
      })
      .catch(() => { message.error('Failed to load campaigns.'); setCampaignId(0); });
  }, [get]);

  const load = useCallback(async () => {
    if (campaignId === null) return;
    setLoading(true);
    try {
      const data = await get(`/api/campaigns/reminder-history${campaignId ? `?campaignId=${campaignId}` : ''}`);
      setRuns(data?.runs ?? []);
    } catch (e: any) {
      message.error(e?.message || 'Failed to load the reminder history.');
    } finally {
      setLoading(false);
    }
  }, [get, campaignId]);

  useEffect(() => { load(); }, [load]);

  // Filters apply to the people; a run stays listed while any of its people match
  const q = search.trim().toLowerCase();
  const matchPerson = useCallback((p: Person) =>
    (!status || p.status === status) &&
    (!q || p.email.toLowerCase().includes(q) || (p.name ?? '').toLowerCase().includes(q)), [status, q]);

  const visibleRuns = useMemo(() => runs
    .filter(r => !stage || r.stage === stage)
    .map(r => ({ ...r, people: r.people.filter(matchPerson) }))
    .filter(r => (!status && !q) || r.people.length > 0), [runs, stage, status, q, matchPerson]);

  const totals = useMemo(() => {
    const people = visibleRuns.flatMap(r => r.people);
    return {
      runs: visibleRuns.length,
      emails: people.length,
      people: new Set(people.map(p => p.email)).size,
      failed: people.filter(p => p.status === 'Failed').length,
      suppressed: people.filter(p => p.status === 'Suppressed').length,
    };
  }, [visibleRuns]);

  const byStage = useMemo(() => (Object.keys(STAGES) as StageKey[]).map(key => {
    const stageRuns = runs.filter(r => r.stage === key);
    return { key, runs: stageRuns.length, emails: stageRuns.reduce((n, r) => n + r.people.length, 0) };
  }), [runs]);

  const exportExcel = () => {
    const rows = visibleRuns.flatMap(r => r.people.map(p => ({
      'Reminder date': dateOnly(r.reminderDate),
      Stage: STAGES[r.stage]?.label ?? r.stage,
      Name: p.name ?? '',
      Email: p.email,
      Status: STATUS[p.status]?.label ?? p.status,
      'Sent at (IST)': ist(p.sentAt),
      Error: p.error ?? '',
    })));
    if (!rows.length) { message.info('Nothing to export.'); return; }
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Reminders');
    const name = campaigns.find(c => c.id === campaignId)?.campaignName ?? 'campaign';
    XLSX.writeFile(wb, `Reminder_History_${name.replace(/[^\w-]+/g, '_')}.xlsx`);
  };

  const statusTags = (people: Person[]) => (Object.keys(STATUS) as Status[])
    .map(s => ({ s, n: people.filter(p => p.status === s).length }))
    .filter(x => x.n > 0)
    .map(({ s, n }) => <Tag key={s} color={STATUS[s].color}>{STATUS[s].label}: {n}</Tag>);

  const runColumns = [
    {
      title: 'Reminder date', dataIndex: 'reminderDate', key: 'reminderDate', width: 140,
      sorter: (a: Run, b: Run) => a.reminderDate.localeCompare(b.reminderDate),
      defaultSortOrder: 'descend' as const,
      render: (v: string) => <Text strong>{dateOnly(v)}</Text>,
    },
    {
      title: 'Stage', dataIndex: 'stage', key: 'stage', width: 200,
      render: (v: StageKey) => <Tag color={STAGES[v]?.color}>{STAGES[v]?.label ?? v}</Tag>,
    },
    {
      title: 'Ran at (IST)', key: 'ranAt', width: 200,
      render: (_: unknown, r: Run) => (
        <Space direction="vertical" size={0}>
          <Text>{ist(r.claimedAt)}</Text>
          {!r.finishedAt && <Text type="warning" style={{ fontSize: 12 }}>Did not finish</Text>}
        </Space>
      ),
    },
    {
      title: 'Emails', key: 'emails', width: 90, align: 'right' as const,
      render: (_: unknown, r: Run) => r.people.length,
    },
    { title: 'Result', key: 'result', render: (_: unknown, r: Run) => <Space wrap size={4}>{statusTags(r.people)}</Space> },
  ];

  const personColumns = [
    { title: 'Name', dataIndex: 'name', key: 'name', render: (v: string | null) => v || <Text type="secondary">—</Text> },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 190,
      render: (v: Status, p: Person) => (
        <Tooltip title={p.error || STATUS[v]?.help}>
          <Tag color={STATUS[v]?.color}>{STATUS[v]?.label ?? v}</Tag>
        </Tooltip>
      ),
    },
    { title: 'Sent at (IST)', dataIndex: 'sentAt', key: 'sentAt', width: 200, render: (v: string) => ist(v) },
  ];

  return (
    <div>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Review reminders are sent automatically at 5:00 pm IST, two days before each stage ends and on its last day"
        description="Only people who still had a review waiting at that stage get one. Each run below can be expanded to see who got the email and when."
      />

      <Space wrap style={{ marginBottom: 16 }}>
        <Select
          style={{ width: 260 }}
          placeholder="Campaign"
          value={campaignId || undefined}
          onChange={setCampaignId}
          options={campaigns.map(c => ({
            value: c.id,
            label: `${c.campaignName || `Campaign ${c.id}`}${c.campaignStatus === 'active' ? ' (active)' : ''}`,
          }))}
        />
        <Select
          allowClear style={{ width: 220 }} placeholder="All stages" value={stage} onChange={setStage}
          options={(Object.keys(STAGES) as StageKey[]).map(k => ({ value: k, label: STAGES[k].label }))}
        />
        <Select
          allowClear style={{ width: 200 }} placeholder="All statuses" value={status} onChange={setStatus}
          options={(Object.keys(STATUS) as Status[]).map(k => ({ value: k, label: STATUS[k].label }))}
        />
        <Input.Search allowClear style={{ width: 260 }} placeholder="Search name or email"
          value={search} onChange={e => setSearch(e.target.value)} />
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
        <Button icon={<FileExcelOutlined />} onClick={exportExcel} disabled={!totals.emails}>Export to Excel</Button>
      </Space>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {byStage.map(s => (
          <Col xs={12} md={6} key={s.key}>
            <Card size="small" hoverable onClick={() => setStage(stage === s.key ? undefined : s.key)}
              style={stage === s.key ? { borderColor: '#8629A4' } : undefined}>
              <Statistic title={STAGES[s.key].label} value={s.runs} suffix={s.runs === 1 ? 'run' : 'runs'} />
              <Text type="secondary" style={{ fontSize: 12 }}>{s.emails} email{s.emails === 1 ? '' : 's'}</Text>
            </Card>
          </Col>
        ))}
      </Row>

      <Space size="large" style={{ marginBottom: 12 }}>
        <Text>{totals.runs} run{totals.runs === 1 ? '' : 's'}</Text>
        <Text>{totals.emails} email{totals.emails === 1 ? '' : 's'} to {totals.people} {totals.people === 1 ? 'person' : 'people'}</Text>
        {totals.failed > 0 && <Text type="danger">{totals.failed} failed</Text>}
        {totals.suppressed > 0 && <Text type="warning">{totals.suppressed} not sent (non-production)</Text>}
      </Space>

      <Table<Run>
        rowKey="id"
        loading={loading}
        dataSource={visibleRuns}
        columns={runColumns}
        size="middle"
        pagination={{ pageSize: 20, hideOnSinglePage: true }}
        locale={{ emptyText: 'No reminders have been sent for this campaign yet.' }}
        expandable={{
          rowExpandable: r => r.people.length > 0,
          expandedRowRender: r => (
            <Table<Person>
              rowKey={p => `${p.runId}-${p.email}`}
              dataSource={r.people}
              columns={personColumns}
              size="small"
              pagination={r.people.length > 20 ? { pageSize: 20 } : false}
            />
          ),
        }}
        scroll={{ x: 900 }}
      />
    </div>
  );
};

export default ReminderHistory;
